import type { ReactNode } from 'react'

/**
 * Page frame: a one-line header (title · context · freshness · actions) and a
 * content area that fills the rest of the screen. No max-width — lists and
 * panels scroll inside the content area, not the page.
 */
export function Page({ title, hint, crumb, context, actions, freshness, children }: {
  /** a string, or rich text (e.g. a mono operation id) — then pass `hint` for the tooltip */
  title: ReactNode
  /** breadcrumb before the title, e.g. a link back to the list ("Panels /") */
  crumb?: ReactNode
  /** longer explanation, shown as the title's tooltip instead of a subtitle paragraph */
  hint?: string
  /** page context: object search, parameters, step rail */
  context?: ReactNode
  /** primary actions, right-aligned */
  actions?: ReactNode
  /** data freshness, right-aligned before the actions */
  freshness?: ReactNode
  children: ReactNode
}) {
  return (
    <main className="flex h-full min-h-0 flex-col">
      <header className="flex min-h-12 shrink-0 items-center gap-3 border-b border-line px-4 py-2">
        {crumb && <div className="flex shrink-0 items-center gap-1 text-[13px] text-text-3">{crumb}</div>}
        <h1 className="min-w-0 shrink truncate text-[15px] font-semibold text-foreground" title={hint ?? (typeof title === 'string' ? title : undefined)}>
          {title}
        </h1>
        <div className="flex min-w-0 flex-1 items-center gap-2">{context}</div>
        {freshness && <div className="shrink-0 text-[12px] text-text-3">{freshness}</div>}
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </header>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </main>
  )
}
